/**
 * Validates if user module record matches base module template structure.
 * 
 * Compares the structural integrity of a user's module record against the base
 * template to detect if the module has diverged from its original structure.
 * Excludes user response data from comparison to focus on structural changes.
 * 
 * @param {Object} userModuleRecord - User's saved module data with responses
 * @param {Object} baseModuleTemplate - Original module template structure
 * @returns {boolean} True if structures match, false if diverged
 * 
 * @example
 * // Check if user module is still compatible
 * const isValid = validateModuleMatch(userRecord, baseTemplate);
 * if (!isValid) {
 *   // Module needs to be reset or migrated
 *   await resetUserModule(moduleId);
 * }
 * 
 * @since 1.0.0
 * @memberof CurriculumUtils
 */
export const validateModuleMatch = (userModuleRecord, baseModuleTemplate) => {
  function excludeResponseKeys(obj) {
    return JSON.stringify(obj, (key, value) => {
      // If the key is "userResponse", exclude it from the final string
      if (key === "userResponse") {
        return undefined
      }

      return value
    })
  }

  function areObjectsIdentical(obj1, obj2) {
    // Convert both objects to strings with "userResponse" keys excluded
    const filteredObj1 = excludeResponseKeys(obj1)
    const filteredObj2 = excludeResponseKeys(obj2)

    // Compare the stringified versions
    return filteredObj1 === filteredObj2
  }

  return areObjectsIdentical(userModuleRecord, baseModuleTemplate)
}

/**
 * Counts words in free-text responses using any whitespace as delimiter.
 * Handles single-word, newline-delimited, and extra-space inputs safely.
 *
 * @param {string} text - Free-text response to count.
 * @returns {number} Number of detected words.
 */
export const getWordCount = (text = "") => {
  if (typeof text !== "string") {
    return 0
  }

  const normalized = text.trim()
  if (!normalized) {
    return 0
  }

  return normalized.split(/\s+/).filter(Boolean).length
}

/**
 * Validates component response completion based on component type.
 * 
 * Determines whether a student has provided a complete response for a given
 * component based on the component's specific requirements and validation rules.
 * 
 * @param {Object} component - Component data with response information
 * @param {string} component.componentId - Component type identifier (QM1, QO1, QC1, T1, etc.)
 * @param {*} component.userResponse - Student's response data (varies by component type)
 * @param {number} [component.responseMinimum] - Minimum word count for QO1 components
 * @param {Array<Array<Object>>} [component.rows] - Row data for table components (T1)
 * 
 * @returns {boolean} True if response is complete, false otherwise
 * 
 * @example
 * // Check multiple choice completion
 * const mcComponent = {
 *   componentId: "QM1",
 *   userResponse: 2 // Selected option index
 * };
 * const isComplete = checkComponentResponseCompletion(mcComponent); // true
 * 
 * @example
 * // Check open response with minimum word requirement
 * const openComponent = {
 *   componentId: "QO1", 
 *   userResponse: "This is a short response",
 *   responseMinimum: 10
 * };
 * const isComplete = checkComponentResponseCompletion(openComponent); // false (< 10 words)
 * 
 * @example
 * // Check table component (recursive validation)
 * const tableComponent = {
 *   componentId: "T1",
 *   rows: [
 *     [{ componentId: "QM1", userResponse: 1 }],
 *     [{ componentId: "QO1", userResponse: "Answer here" }]
 *   ]
 * };
 * const isComplete = checkComponentResponseCompletion(tableComponent); // true
 * 
 * @since 1.0.0
 * @memberof CurriculumUtils
 */
export const checkComponentResponseCompletion = (component) => {
  const { componentId } = component

  switch (componentId) {
    case "QM1":
      return component.userResponse !== null
    case "QO1":
      if (!component.responseMinimum) {
        return component.userResponse !== null
      } else {
        return component.userResponse !== null && getWordCount(component.userResponse) >= component.responseMinimum
      }
    case "QC1":
      return component.userResponse?.length > 0
    case "T1":
      // flatten rows of table into single component and recursively check components within
      const allRows = component.rows.flat()
      const allRowsComplete = allRows.every((row) => {
        return checkComponentResponseCompletion(row)
      })
      return allRowsComplete
    default:
      return true
  }
}

// function to calculate score
export const calculateModuleScore = (moduleState) => {
  const extractQuestions = (data) => {
    let result = []

    function traverse(obj) {
      if (typeof obj !== "object" || obj === null) return

      if (obj.type === "QUESTION") {
        result.push(obj)
      }

      if (Array.isArray(obj)) {
        obj.forEach((item) => traverse(item))
      } else {
        Object.values(obj).forEach((value) => traverse(value))
      }
    }

    traverse(data)
    return result
  }

  const questions = extractQuestions(moduleState)
  const totalQuestions = questions.length

  if (totalQuestions === 0) {
    return 100
  }

  const questionScores = questions.map((question) => {
    const { componentId, userResponse } = question

    switch (componentId) {
      case "QM1":
        const isCorrect = !!question?.options?.[userResponse]?.isCorrect

        if (isCorrect || question.isGradingDisabled) {
          return 1
        } else {
          return 0
        }

      case "QO1":
        // open response question, is correct if meets minimum word count
        if (getWordCount(userResponse) >= question.responseMinimum) {
          return 1
        } else {
          return 0
        }

      case "QC1":
        if (!question?.options || !Array.isArray(question.userResponse)) {
          return 0
        }

        // if question is not graded consider correct
        if (question.isGradingDisabled) {
          return 1
        }

        const correctAnswers = new Set()
        const incorrectAnswers = new Set()

        question.options.forEach((option, index) => {
          if (option.isCorrect) {
            correctAnswers.add(index)
          } else {
            incorrectAnswers.add(index)
          }
        })

        const userResponses = new Set(question.userResponse)

        // Check if user selected all correct answers and no incorrect ones
        if ([...correctAnswers].every((answer) => userResponses.has(answer)) && ![...userResponses].some((answer) => incorrectAnswers.has(answer))) {
          return 1
        }

        return 0

      default:
        return 1
    }
  })

  const totalScore = questionScores.reduce((acc, score) => acc + score, 0)
  const percentageScore = Math.ceil((totalScore / totalQuestions) * 100)
  return percentageScore
}

// function to calculate score
export const getCorrectQuestionCountAndTotal = (moduleState) => {
  const extractQuestions = (data) => {
    let result = []

    function traverse(obj) {
      if (typeof obj !== "object" || obj === null) return

      if (obj.type === "QUESTION") {
        result.push(obj)
      }

      if (Array.isArray(obj)) {
        obj.forEach((item) => traverse(item))
      } else {
        Object.values(obj).forEach((value) => traverse(value))
      }
    }

    traverse(data)
    return result
  }

  const questions = extractQuestions(moduleState)
  const totalQuestions = questions.length

  if (totalQuestions === 0) {
    return 100
  }

  const questionScores = questions.map((question) => {
    const { componentId, userResponse } = question

    switch (componentId) {
      case "QM1":
        const isCorrect = !!question?.options?.[userResponse]?.isCorrect

        if (isCorrect || question.isGradingDisabled) {
          return 1
        } else {
          return 0
        }

      case "QO1":
        // open response question, is correct if meets minimum word count
        if (getWordCount(userResponse) >= question.responseMinimum) {
          return 1
        } else {
          return 0
        }

      case "QC1":
        if (!question?.options || !Array.isArray(question.userResponse)) {
          return 0
        }

        // if question is not graded consider correct
        if (question.isGradingDisabled) {
          return 1
        }

        const correctAnswers = new Set()
        const incorrectAnswers = new Set()

        question.options.forEach((option, index) => {
          if (option.isCorrect) {
            correctAnswers.add(index)
          } else {
            incorrectAnswers.add(index)
          }
        })

        const userResponses = new Set(question.userResponse)

        // Check if user selected all correct answers and no incorrect ones
        if ([...correctAnswers].every((answer) => userResponses.has(answer)) && ![...userResponses].some((answer) => incorrectAnswers.has(answer))) {
          return 1
        }

        return 0

      default:
        return 1
    }
  })

  const totalCorrect = questionScores.reduce((acc, score) => acc + score, 0)

  return {
    totalCorrect,
    totalQuestions
  }
}

export const truncateText = (text, limit) => {
  if (!text) return "";
  return text.length > limit ? `${text.substring(0, limit)}...` : text;
};

export const moduleCategories = [
  { name: "Career and Self Development", key: "career_and_self_dev", type: "ADULT" },
  { name: "Communication", key: "communication", type: "ADULT" },
  { name: "Critical Thinking", key: "critical_thinking", type: "ADULT" },
  { name: "Equity and Inclusion", key: "equity_and_inclusion", type: "ADULT" },
  { name: "Leadership", key: "leadership", type: "ADULT" },
  { name: "Professionalism", key: "professionalism", type: "ADULT" },
  { name: "Teamwork", key: "teamwork", type: "ADULT" },
  { name: "Technology", key: "technology", type: "ADULT" },
  { name: "6th Grade", key: "6th_grade", type: "K-12" },
  { name: "7th Grade", key: "7th_grade", type: "K-12" },
  { name: "8th Grade", key: "8th_grade", type: "K-12" },
  { name: "9th Grade", key: "9th_grade", type: "K-12" },
  { name: "10th Grade", key: "10th_grade", type: "K-12" },
  { name: "11th Grade", key: "11th_grade", type: "K-12" },
  { name: "12th Grade", key: "12th_grade", type: "K-12" },
]

export const hasHTMLContent = (htmlString) => {

  if (typeof window === "undefined") {
    return false;
  }

  // Create a temporary element to parse the HTML
  const div = document.createElement('div');
  div.innerHTML = htmlString;

  // Get the text content and trim whitespace
  const text = div.textContent || div.innerText || '';
  return text.trim().length > 0;
}
